import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Mail,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Users,
  FolderKanban,
  Filter,
  RotateCcw,
  Trash2,
  Eye,
  Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// API-Funktionen
const emailCsvImportAPI = {
  getJobs: (params = {}) => api.get("/email-csv-import/jobs", { params }),
  getJob: (id) => api.get(`/email-csv-import/jobs/${id}`),
  retryJob: (id) => api.post(`/email-csv-import/jobs/${id}/retry`),
  deleteJob: (id) => api.delete(`/email-csv-import/jobs/${id}`),
  getStats: () => api.get("/email-csv-import/stats"),
  upload: (file, metadata = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(
      `/email-csv-import/upload?sender=${encodeURIComponent(metadata.sender || "manual@upload.local")}&subject=${encodeURIComponent(metadata.subject || "Manueller CSV-Import")}&source=${encodeURIComponent(metadata.source || "Wattfox")}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  }
};

// Status-Badge Komponente
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: "Ausstehend", variant: "secondary", icon: Clock },
    processing: { label: "Verarbeitung", variant: "default", icon: Loader2 },
    success: { label: "Erfolgreich", variant: "success", icon: CheckCircle },
    failed: { label: "Fehlgeschlagen", variant: "destructive", icon: XCircle },
    partial: { label: "Teilweise", variant: "warning", icon: AlertTriangle },
    skipped: { label: "Übersprungen", variant: "outline", icon: AlertTriangle },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant={config.variant} 
      className={`flex items-center gap-1 ${
        status === "success" ? "bg-green-100 text-green-800 border-green-200" :
        status === "failed" ? "bg-red-100 text-red-800 border-red-200" :
        status === "partial" ? "bg-yellow-100 text-yellow-800 border-yellow-200" :
        status === "processing" ? "bg-blue-100 text-blue-800 border-blue-200" :
        ""
      }`}
    >
      <Icon className={`h-3 w-3 ${status === "processing" ? "animate-spin" : ""}`} />
      {config.label}
    </Badge>
  );
};

// Statistik-Karte Komponente
const StatCard = ({ title, value, icon: Icon, color = "blue" }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`h-5 w-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Job-Details Dialog
const JobDetailsDialog = ({ job, open, onOpenChange }) => {
  if (!job) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Import-Details
          </DialogTitle>
          <DialogDescription>
            Job-ID: {job.id}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Status */}
          <div className="flex items-center gap-2">
            <span className="font-medium">Status:</span>
            <StatusBadge status={job.status} />
          </div>
          
          {/* E-Mail-Info */}
          <div className="border rounded-lg p-4 space-y-2">
            <h4 className="font-medium">E-Mail-Informationen</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Absender:</span>
                <p>{job.sender || "N/A"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Betreff:</span>
                <p>{job.subject || "N/A"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Empfangen:</span>
                <p>{job.received_at ? new Date(job.received_at).toLocaleString("de-DE") : "N/A"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Message-ID:</span>
                <p className="truncate text-xs">{job.email_message_id || "N/A"}</p>
              </div>
            </div>
          </div>
          
          {/* Datei-Info */}
          <div className="border rounded-lg p-4 space-y-2">
            <h4 className="font-medium">Datei-Informationen</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Dateiname:</span>
                <p>{job.attachment_filename || "N/A"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Größe:</span>
                <p>{job.attachment_size ? `${(job.attachment_size / 1024).toFixed(1)} KB` : "N/A"}</p>
              </div>
            </div>
          </div>
          
          {/* Ergebnisse */}
          <div className="border rounded-lg p-4 space-y-2">
            <h4 className="font-medium">Import-Ergebnisse</h4>
            <div className="grid grid-cols-4 gap-2 text-sm text-center">
              <div className="p-2 bg-green-50 rounded">
                <p className="text-2xl font-bold text-green-600">{job.imported_records_count || 0}</p>
                <p className="text-xs text-muted-foreground">Importiert</p>
              </div>
              <div className="p-2 bg-blue-50 rounded">
                <p className="text-2xl font-bold text-blue-600">{job.created_customer_count || 0}</p>
                <p className="text-xs text-muted-foreground">Kunden</p>
              </div>
              <div className="p-2 bg-purple-50 rounded">
                <p className="text-2xl font-bold text-purple-600">{job.created_project_count || 0}</p>
                <p className="text-xs text-muted-foreground">Projekte</p>
              </div>
              <div className="p-2 bg-yellow-50 rounded">
                <p className="text-2xl font-bold text-yellow-600">{job.skipped_count || 0}</p>
                <p className="text-xs text-muted-foreground">Übersprungen</p>
              </div>
            </div>
          </div>
          
          {/* Fehler */}
          {(job.error_message || (job.errors && job.errors.length > 0)) && (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <h4 className="font-medium text-red-800 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Fehler
              </h4>
              {job.error_message && (
                <p className="text-sm text-red-700 mt-1">{job.error_message}</p>
              )}
              {job.errors && job.errors.length > 0 && (
                <ul className="text-sm text-red-700 mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {job.errors.map((err, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <span className="text-red-500">•</span>
                      {err}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          {/* Warnungen */}
          {job.warnings && job.warnings.length > 0 && (
            <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
              <h4 className="font-medium text-yellow-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Warnungen
              </h4>
              <ul className="text-sm text-yellow-700 mt-2 space-y-1 max-h-40 overflow-y-auto">
                {job.warnings.map((warn, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <span className="text-yellow-500">•</span>
                    {warn}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Timestamps */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p>Erstellt: {job.created_at ? new Date(job.created_at).toLocaleString("de-DE") : "N/A"}</p>
            {job.started_at && <p>Gestartet: {new Date(job.started_at).toLocaleString("de-DE")}</p>}
            {job.finished_at && <p>Beendet: {new Date(job.finished_at).toLocaleString("de-DE")}</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Inbound-Status Komponente
const InboundStatusCard = ({ onRefresh }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await api.get("/webhooks/email-inbound/status");
        setStatus(response.data);
      } catch (error) {
        console.error("Failed to load inbound status:", error);
        setStatus({ status: "error", error: error.message });
      } finally {
        setLoading(false);
      }
    };
    loadStatus();
  }, []);

  if (loading) {
    return (
      <Card className="border-2 border-dashed">
        <CardContent className="py-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isActive = status?.status === "active";

  return (
    <Card className={`border-2 ${isActive ? "border-green-200 bg-green-50/50" : "border-yellow-200 bg-yellow-50/50"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className={`h-5 w-5 ${isActive ? "text-green-600" : "text-yellow-600"}`} />
          Automatischer E-Mail-Import
        </CardTitle>
        <CardDescription>
          Eingehende E-Mails werden automatisch verarbeitet
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant={isActive ? "success" : "warning"} className={isActive ? "bg-green-100 text-green-800" : ""}>
            {isActive ? "Aktiv" : "Nicht konfiguriert"}
          </Badge>
          {status?.signature_verification === "enabled" && (
            <Badge variant="outline" className="text-xs">
              <CheckCircle className="h-3 w-3 mr-1" />
              Signatur-Verifikation
            </Badge>
          )}
        </div>
        
        <div className="text-sm text-muted-foreground space-y-1">
          <p><strong>Provider:</strong> {status?.provider || "Resend Inbound"}</p>
          <p><strong>Webhook-URL:</strong></p>
          <code className="text-xs bg-gray-100 px-2 py-1 rounded block mt-1 break-all">
            {window.location.origin.replace("://", "s://").replace(":3000", "")}/api/webhooks/email-inbound
          </code>
        </div>

        <div className="pt-2 border-t text-xs text-muted-foreground">
          <p className="font-medium mb-1">Einrichtung:</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>MX-Record für Inbound-Domain einrichten</li>
            <li>Webhook in Resend Dashboard erstellen</li>
            <li>Event "email.received" auswählen</li>
            <li>E-Mails an die Inbound-Adresse weiterleiten</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};

// Hauptkomponente
export default function EmailCsvImportPage() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [showManualUpload, setShowManualUpload] = useState(false);
  
  // Daten laden
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [jobsRes, statsRes] = await Promise.all([
        emailCsvImportAPI.getJobs({ status: statusFilter !== "all" ? statusFilter : undefined }),
        emailCsvImportAPI.getStats()
      ]);
      
      setJobs(jobsRes.data.jobs || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load import data:", error);
      toast.error("Fehler beim Laden der Import-Daten");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Datei-Upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Nur CSV-Dateien werden unterstützt");
      return;
    }
    
    setUploading(true);
    try {
      const result = await emailCsvImportAPI.upload(file, {
        sender: "manual@upload.local",
        subject: `Manueller Upload: ${file.name}`,
        source: "Wattfox"
      });
      
      if (result.data.success) {
        toast.success(
          `Import erfolgreich: ${result.data.imported_count || 0} Datensätze importiert, ` +
          `${result.data.created_customers || 0} Kunden erstellt`
        );
      } else if (result.data.skipped) {
        toast.warning("Datei wurde bereits importiert");
      } else {
        toast.error(result.data.error || "Import fehlgeschlagen");
      }
      
      loadData();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };
  
  // Job erneut versuchen
  const handleRetry = async (jobId) => {
    setRetrying(jobId);
    try {
      const result = await emailCsvImportAPI.retryJob(jobId);
      
      if (result.data.success) {
        toast.success("Import erfolgreich wiederholt");
      } else {
        toast.error(result.data.error || "Wiederholung fehlgeschlagen");
      }
      
      loadData();
    } catch (error) {
      console.error("Retry failed:", error);
      toast.error(error.response?.data?.detail || "Wiederholung fehlgeschlagen");
    } finally {
      setRetrying(null);
    }
  };
  
  // Job löschen
  const handleDelete = async (jobId) => {
    if (!window.confirm("Möchten Sie diesen Import-Job wirklich löschen?")) return;
    
    try {
      await emailCsvImportAPI.deleteJob(jobId);
      toast.success("Import-Job gelöscht");
      loadData();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };
  
  return (
    <div className="p-6 space-y-6" data-testid="email-csv-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6" />
            E-Mail CSV-Import
          </h1>
          <p className="text-muted-foreground">
            Automatischer Lead-Import aus eingehenden E-Mails (Wattfox)
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setShowManualUpload(!showManualUpload)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Manueller Upload
          </Button>
        </div>
      </div>

      {/* Inbound Status + Manual Upload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InboundStatusCard onRefresh={loadData} />
        
        {/* Manual Upload Card */}
        <Collapsible open={showManualUpload} onOpenChange={setShowManualUpload}>
          <Card className="border-2 border-dashed">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50/50 transition-colors pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-gray-500" />
                    Manueller CSV-Upload
                  </span>
                  {showManualUpload ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </CardTitle>
                <CardDescription>
                  Alternative: CSV-Datei direkt hochladen (ohne E-Mail)
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Wird hochgeladen...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="h-8 w-8 text-gray-400" />
                        <p className="font-medium">CSV-Datei auswählen</p>
                        <p className="text-sm text-muted-foreground">
                          Wattfox-Format (UTF-16LE, Semikolon-separiert)
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
      
      {/* Statistiken */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gesamt</p>
                  <p className="text-2xl font-bold">{stats.total_jobs}</p>
                </div>
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Erfolgreich</p>
                  <p className="text-2xl font-bold text-green-600">{stats.success}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Fehlgeschlagen</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ausstehend</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Kunden erstellt</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.total_customers_created}</p>
                </div>
                <Users className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Projekte erstellt</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.total_projects_created}</p>
                </div>
                <FolderKanban className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filter */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Import-Jobs</CardTitle>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="processing">Verarbeitung</SelectItem>
                  <SelectItem value="success">Erfolgreich</SelectItem>
                  <SelectItem value="failed">Fehlgeschlagen</SelectItem>
                  <SelectItem value="partial">Teilweise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Keine Import-Jobs gefunden</p>
              <p className="text-sm">Laden Sie eine CSV-Datei hoch oder warten Sie auf eingehende E-Mails</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Absender</TableHead>
                  <TableHead>Betreff</TableHead>
                  <TableHead>Datei</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Importiert</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="text-sm">
                      {job.created_at ? new Date(job.created_at).toLocaleDateString("de-DE") : "N/A"}
                      <br />
                      <span className="text-xs text-muted-foreground">
                        {job.created_at ? new Date(job.created_at).toLocaleTimeString("de-DE") : ""}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate" title={job.sender}>
                      {job.sender || "N/A"}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate" title={job.subject}>
                      {job.subject || "N/A"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.attachment_filename || "N/A"}
                      {job.attachment_size && (
                        <span className="text-xs text-muted-foreground block">
                          {(job.attachment_size / 1024).toFixed(1)} KB
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={job.status} />
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-medium">{job.imported_records_count || 0}</span>
                      <span className="text-muted-foreground text-xs block">
                        {job.created_customer_count || 0} Kunden
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedJob(job);
                            setDetailsOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {(job.status === "failed" || job.status === "partial") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRetry(job.id)}
                            disabled={retrying === job.id}
                          >
                            {retrying === job.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(job.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Job-Details Dialog */}
      <JobDetailsDialog
        job={selectedJob}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </div>
  );
}
